/**
 * Local Database - Journal Entry Operations
 *
 * CRUD operations for journal entries.
 */

import {
  localStore,
  isElectron,
  getElectronWindow,
  saveToLocalStorage,
  getNextSequence,
} from './store'

import type { LocalJournalEntry, LocalJournalEntryLine } from './types'

/**
 * Get all journal entries from local database
 * @param orgId Organization ID
 * @param ledgerId Optional ledger ID to filter by (defaults to primary ledger)
 */
export function getLocalJournalEntries(orgId: string, ledgerId?: string): Promise<LocalJournalEntry[]> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getJournalEntries(orgId, ledgerId)
  }

  const targetLedgerId = ledgerId ?? localStore.defaultLedgerId

  return Promise.resolve(
    Array.from(localStore.journalEntries.values())
      /* v8 ignore next */
      .filter(e => e.orgId === orgId && (!targetLedgerId || e.ledgerId === targetLedgerId))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  )
}

/**
 * Get journal entry by ID
 */
export function getLocalJournalEntry(orgId: string, entryId: string): Promise<LocalJournalEntry | null> {
  if (isElectron) {
    return getElectronWindow().electronAPI.db.getJournalEntry(orgId, entryId)
  }

  const entry = localStore.journalEntries.get(entryId)
  return Promise.resolve(entry?.orgId === orgId ? entry : null)
}

/**
 * Validate that a journal entry is balanced (total debits = total credits)
 */
function validateJournalEntryBalance(lines: LocalJournalEntryLine[]): void {
  const totalDebit = lines.reduce((sum, line) => sum + (line.debitAmount || 0), 0)
  const totalCredit = lines.reduce((sum, line) => sum + (line.creditAmount || 0), 0)

  if (Math.abs(totalDebit - totalCredit) > 0) {
    throw new Error(`Journal entry must be balanced. Debits: ${totalDebit / 100}, Credits: ${totalCredit / 100}`)
  }
}

/**
 * Create a new journal entry
 */
export function createLocalJournalEntry(
  orgId: string,
  data: Omit<LocalJournalEntry, 'id' | 'orgId' | 'entryNumber' | 'createdAt' | 'updatedAt'>
): Promise<LocalJournalEntry> {
  // Validate balance before creating
  validateJournalEntryBalance(data.lines)

  const id = crypto.randomUUID()
  const now = new Date().toISOString()

  // Generate entry number
  const seq = getNextSequence(orgId, 'journal_entry')
  const entryNumber = `JE-${String(seq).padStart(5, '0')}`

  const entry: LocalJournalEntry = {
    ...data,
    id,
    orgId,
    entryNumber,
    createdAt: now,
    updatedAt: now,
  }

  if (isElectron) {
    return getElectronWindow().electronAPI.db.createJournalEntry(entry)
  }

  localStore.journalEntries.set(id, entry)
  saveToLocalStorage()
  return Promise.resolve(entry)
}

/**
 * Update a journal entry
 */
export async function updateLocalJournalEntry(
  orgId: string,
  entryId: string,
  data: Partial<LocalJournalEntry>
): Promise<LocalJournalEntry> {
  const existing = await getLocalJournalEntry(orgId, entryId)
  if (!existing) {throw new Error('Entry not found')}

  const updated: LocalJournalEntry = {
    ...existing,
    ...data,
    id: existing.id,
    orgId: existing.orgId,
    entryNumber: existing.entryNumber,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  }

  if (isElectron) {
    return getElectronWindow().electronAPI.db.updateJournalEntry(updated)
  }

  localStore.journalEntries.set(entryId, updated)
  saveToLocalStorage()
  return updated
}

/**
 * Post a journal entry
 */
export function postLocalJournalEntry(orgId: string, entryId: string): Promise<LocalJournalEntry> {
  const n = new Date()
  const postDate = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
  /* v8 ignore next */
  if (!postDate) {throw new Error('Failed to generate post date')}
  return updateLocalJournalEntry(orgId, entryId, {
    status: 'posted',
    postDate,
  })
}

/**
 * Void a journal entry
 */
export function voidLocalJournalEntry(orgId: string, entryId: string): Promise<LocalJournalEntry> {
  return updateLocalJournalEntry(orgId, entryId, {
    status: 'voided',
  })
}

export function deleteLocalJournalEntry(_orgId: string, entryId: string): Promise<void> {
  localStore.journalEntries.delete(entryId)
  saveToLocalStorage()
  return Promise.resolve()
}
